/**
 * `Help` — the pane's third tab (Amendment 1 §1/§A.3): "cards from the `help_cards` table:
 * title, icon, 3–5 numbered steps, 'Take me there'."
 *
 * Content-driven on purpose — "adding a card requires no code change" (§G.3) — so this
 * component knows nothing about what cards exist, only how to draw whatever
 * `GET intelligence/help` answers and how to act on a card's `target`. The five target
 * shapes the backend can send (`{tab}`, `+use_case`, `+step`, `{tool}`, `{action:
 * "draw_aoi"}`) are exactly `IntelHelpTarget`'s fields; `applyTarget` below is the one place
 * that reads them, so a sixth kind is a case added here, not a new component.
 *
 * Presentation standard (design §1 acceptance): one card per block, each titled, an icon
 * that matches the rail's set, numbered steps, one accent (`--brand`) and no scroller of its
 * own — `#adBody` is the dock's only scroller, so cards simply stack.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IntelHelpCard } from '../../lib/api';
import { Icon, ICONS, type IconName } from '../../lib/icons';
import { useAppStore } from '../../lib/state';
import { TOOL_IDS, type ToolId } from '../../lib/analysis';
import { useUseCases } from './api';
import { useIntelHelp } from './useIntelHelp';

function iconFor(name: string): IconName {
  return name in ICONS ? (name as IconName) : 'info';
}

/** The three tabs `Tabs.tsx` actually renders — a `target.tab` outside this set matches
 * none of them, leaving every panel hidden (§Tabs.tsx `TAB_ORDER`), so it must be rejected
 * before `setIntelTab` ever runs. */
const TAB_SET = new Set(['usecases', 'tools', 'help']);

export function Help() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useIntelHelp();
  const { data: useCases } = useUseCases();
  const [invalidCard, setInvalidCard] = useState<string | null>(null);
  const setIntelTab = useAppStore((s) => s.setIntelTab);
  const setIntelUseCase = useAppStore((s) => s.setIntelUseCase);
  const setIntelStep = useAppStore((s) => s.setIntelStep);
  const setIntelDockTool = useAppStore((s) => s.setIntelDockTool);

  /**
   * A card's `target` is content-driven (`help_cards` table, §G.3) — nothing here stops an
   * operator from typo-ing a tab, a tool id, a use case, or a step in the seed data. Rather
   * than let a bad value partially apply (or, worse, land on a tab `Tabs.tsx` doesn't
   * recognise and hide every panel), validate the whole target before touching any store
   * state; on failure the card shows an inline message and nothing else happens.
   */
  const isValidTarget = (target: IntelHelpCard['target']) => {
    if (!TAB_SET.has(target.tab)) return false;
    if (target.tool !== undefined && !TOOL_IDS.includes(target.tool as ToolId)) return false;
    if (target.use_case !== undefined && !(useCases ?? []).some((uc) => uc.id === target.use_case))
      return false;
    if (
      target.step !== undefined &&
      !(Number.isInteger(target.step) && target.step > 0)
    )
      return false;
    return true;
  };

  /**
   * "Take me there" — applies a card's `target` in the order that survives
   * `setIntelUseCase`'s own reset: the tab first (harmless either order), the use case
   * (which resets `intel.step` back to 0 along with everything else it isn't keeping), and
   * only then the step, so it lands after the reset that would otherwise erase it.
   */
  const applyTarget = (cardId: string, target: IntelHelpCard['target']) => {
    if (!isValidTarget(target)) {
      setInvalidCard(cardId);
      return;
    }
    setInvalidCard(null);
    setIntelTab(target.tab);
    if (target.use_case) setIntelUseCase(target.use_case);
    if (target.step !== undefined) setIntelStep(target.step);
    // `draw_aoi` (§A.3's "draw an area of interest" card) opens the AOI tool itself — the
    // draw control is that tool's own first move, not a separate mode to switch on.
    const tool = target.action === 'draw_aoi' ? 'aoi' : target.tool;
    if (tool) setIntelDockTool(tool as ToolId);
  };

  return (
    <div className="intel-help" data-testid="intel-help">
      <h4 className="intel-help-title">{t('intel.help.title')}</h4>
      {isLoading ? (
        <p className="note">{t('intel.help.loading')}</p>
      ) : isError ? (
        <p className="note" data-role="error">
          {t('intel.help.error')}
        </p>
      ) : !data?.length ? (
        <p className="note">{t('intel.help.empty')}</p>
      ) : (
        [...data]
          .sort((a, b) => a.order - b.order)
          .map((card) => (
            <section className="help-card" id={`helpCard-${card.id}`} key={card.id}>
              <header className="help-card-head">
                <span className="help-card-ico" aria-hidden="true">
                  <Icon name={iconFor(card.icon)} size={18} />
                </span>
                <h5>{card.title}</h5>
              </header>
              <ol className="help-card-steps">
                {card.steps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
              <button
                type="button"
                className="btn ghost sm"
                id={`helpGo-${card.id}`}
                onClick={() => applyTarget(card.id, card.target)}
              >
                {t('intel.help.goThere')}
              </button>
              {invalidCard === card.id ? (
                <p className="note" data-role="help-invalid" id={`helpInvalid-${card.id}`}>
                  {t('intel.help.unavailable')}
                </p>
              ) : null}
            </section>
          ))
      )}
    </div>
  );
}

export default Help;
